import { cn } from '@fruuit/ui'
import { Check } from 'lucide-react'
import { STAGE_STEPS, stageLevel, stageName } from './format'

/**
 * The five-step stage track (STAT-013): Baseline → Outcome. Reached steps are filled,
 * passed steps show a tick, and the current step is announced in the label.
 */
export function StageTrack({ stage, size = 'md' }: { stage: string; size?: 'sm' | 'md' }) {
  const level = stageLevel(stage)
  return (
    <ol
      aria-label={`Stage: ${stageName(stage)}`}
      className={cn('grid grid-cols-5 gap-1', size === 'md' ? 'mt-4' : 'mt-2')}
    >
      {STAGE_STEPS.map((step, index) => {
        const reached = index <= level
        const current = index === level
        return (
          <li key={step} className="min-w-0" aria-current={current ? 'step' : undefined}>
            <div
              className={cn(
                'h-1.5 rounded-full',
                reached ? 'bg-teal' : 'bg-line',
                current && 'ring-2 ring-teal-200',
              )}
            />
            {size === 'md' ? (
              <div className="mt-2 flex items-center gap-1 text-[11px] leading-tight sm:text-xs">
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                    reached ? 'bg-teal text-white' : 'bg-line-soft text-sage',
                  )}
                >
                  {index < level ? <Check className="size-3" /> : index}
                </span>
                <span
                  className={cn(
                    'truncate',
                    current ? 'font-semibold text-ink' : reached ? 'text-ink-soft' : 'text-sage',
                  )}
                >
                  {step}
                </span>
              </div>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Width classes in 5% steps: the strict CSP has no inline styles (ARCHITECTURE §7), so the
 * bar uses static Tailwind classes instead of a style attribute.
 */
const WIDTHS = [
  'w-[0%]',
  'w-[5%]',
  'w-[10%]',
  'w-[15%]',
  'w-[20%]',
  'w-[25%]',
  'w-[30%]',
  'w-[35%]',
  'w-[40%]',
  'w-[45%]',
  'w-[50%]',
  'w-[55%]',
  'w-[60%]',
  'w-[65%]',
  'w-[70%]',
  'w-[75%]',
  'w-[80%]',
  'w-[85%]',
  'w-[90%]',
  'w-[95%]',
  'w-[100%]',
]

/** Average evidence progress across objectives (UI-013). */
export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const value = Math.min(Math.max(percent, 0), 100)
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="h-1.5 w-full max-w-40 overflow-hidden rounded-full bg-line"
    >
      <div className={cn('h-full rounded-full bg-teal', WIDTHS[Math.round(value / 5)])} />
    </div>
  )
}
